/**
 * PHASE 7 — GLSL sources for the four sky layers.
 *
 * Layer 0 (skybox): fullscreen quad; per fragment the view ray is rebuilt
 * from NDC + fov, rotated ENU -> galactic, and used to sample the
 * equirectangular texture (u: galactic longitude, v: galactic latitude).
 *
 * Layers 1-3 (stars / constellation lines / deep-sky): pure point/line
 * geometry in J2000 equatorial xyz, transformed by a single uMVP uniform
 * built CPU-side (projection ++ camera ++ precession ++ hour angle).
 */

export const SKYBOX_VS = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
  vUv = aPos * 0.5 + 0.5;
}
`;

export const SKYBOX_FS = `
precision mediump float;

uniform sampler2D uSky;
uniform mat3 uCamToWorld;   // device -> ENU (world)
uniform mat3 uEnuToGal;     // ENU -> galactic xyz
uniform float uInvTanHalfFovY;
uniform float uAspect;
uniform float uHorizonFade; // radians of smooth fade across the horizon

varying vec2 vUv;

void main() {
  // Rebuild the view direction in device frame from the NDC position.
  // vUv = 0 at the bottom-left of the viewport, 1 at the top-right; NDC y
  // grows upward (WebGL) and the device +y axis is up, so both axes map
  // monotonically (a mirrored mapping would render the sky upside down
  // relative to the star layer).
  vec3 dirDev = normalize(vec3(
    (vUv.x * 2.0 - 1.0) * uAspect / uInvTanHalfFovY,
    (vUv.y * 2.0 - 1.0) / uInvTanHalfFovY,
    -1.0
  ));
  vec3 dirEnu = uCamToWorld * dirDev;
  vec3 dirGal = uEnuToGal * dirEnu;

  float lon = atan(dirGal.y, dirGal.x);                 // l in [-pi, pi]
  float lat = asin(clamp(dirGal.z, -1.0, 1.0));         // b
  vec2 uv = vec2(0.5 + lon / 6.2831853, (1.5707963 - lat) / 3.1415927);
  vec3 color = texture2D(uSky, uv).rgb;

  float horizon = smoothstep(0.0, uHorizonFade, dirEnu.z);
  color *= horizon;                                     // fade out below-horizon
  color *= 1.6;                                         // lift to compensate dark image

  gl_FragColor = vec4(color, 1.0);
}
`;

export const STARS_VS = `
attribute vec3 aPos;      // J2000 equatorial xyz (unit sphere)
attribute float aSize;    // radius in pixels
attribute vec3 aColor;    // blackbody-tinted rgb

uniform mat4 uMVP;

varying vec3 vColor;

void main() {
  gl_Position = uMVP * vec4(aPos, 1.0);
  gl_PointSize = aSize;
  vColor = aColor;
}
`;

export const STARS_FS = `
precision mediump float;

varying vec3 vColor;

void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d);
  float alpha = smoothstep(0.5, 0.2, r) * 0.92;
  // Hot white core.
  float core = smoothstep(0.22, 0.0, r) * 0.25;
  gl_FragColor = vec4(vColor * (0.85 + core), alpha);
}
`;

export const LINES_VS = `
attribute vec3 aPos;
uniform mat4 uMVP;
void main() {
  gl_Position = uMVP * vec4(aPos, 1.0);
}
`;

export const LINES_FS = `
precision mediump float;
uniform vec4 uColor;
void main() {
  gl_FragColor = uColor;
}
`;

export const DSO_VS = `
attribute vec3 aPos;
attribute float aSize;
uniform mat4 uMVP;
varying float vSize;
void main() {
  gl_Position = uMVP * vec4(aPos, 1.0);
  gl_PointSize = aSize;
  vSize = aSize;
}
`;

export const DSO_FS = `
precision mediump float;

uniform vec3 uColor;
varying float vSize;

void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d);
  float halfR = 0.5;
  float ring = smoothstep(halfR, halfR - 0.06, r) * smoothstep(halfR - 0.28, halfR, r);
  float plus = 0.0;
  if (abs(d.x) < 0.02 && abs(d.y) < halfR) plus += 1.0;
  if (abs(d.y) < 0.02 && abs(d.x) < halfR) plus += 1.0;
  float mask = clamp(ring + plus * 0.7, 0.0, 1.0);
  gl_FragColor = vec4(uColor, mask * 0.9);
}
`;

/** Indexed fullscreen quad (6 indices). */
export const FULLSCREEN_QUAD_POSITIONS = new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]);
export const FULLSCREEN_QUAD_INDICES = new Uint16Array([0, 1, 2, 0, 2, 3]);